import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, Award, Users, Heart, Stethoscope, ShieldCheck, ExternalLink } from "lucide-react";
import SEO from "@/components/SEO";
import { usePageSeo } from "@/hooks/usePageSeo";

const AboutPage = () => {
  const seoOverride = usePageSeo("about");
  const values = [
    {
      icon: Heart,
      title: "Compassionate Care",
      description: "We treat every client with empathy, respect, and dignity, understanding the courage it takes to seek help.",
    },
    {
      icon: Stethoscope,
      title: "Clinical Excellence",
      description: "Maintaining the highest standards of therapeutic practice, backed by continuous education and trauma-informed training.",
    },
    {
      icon: Users,
      title: "Client-Centered",
      description: "Your unique journey guides our approach. We create personalized treatment plans tailored to your needs.",
    },
    {
      icon: Award,
      title: "Evidence-Based",
      description: "Every treatment protocol is grounded in the latest clinical research and proven therapeutic outcomes.",
    },
  ];

  const team = [
    {
      name: "Laura A. Jeanis",
      role: "Licensed Psychotherapist",
      credentials: "LCSW, LICSW, CCTP, CEO",
      bio: "With 11 years of experience in mental health and as a Certified Clinical Trauma Professional, Laura specializes in Ketamine-Assisted Psychotherapy. Her compassionate, trauma-informed approach has helped countless clients find healing and transformation.",
      image: "https://customer-assets.emergentagent.com/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/l4ja8zkq_IMG_4558_Facetune_17-01-2023-21-22-22.jpeg",
    },
  ];

  const certifications = [
    "Licensed Clinical Social Worker (LCSW)",
    "Licensed Independent Clinical Social Worker (LICSW)",
    "Certified Clinical Trauma Professional (CCTP) — Certification Number: 244805",
    "Licensed Psychotherapist in FL — LCSW, License Number: SW20897",
    "Licensed Psychotherapist in ME — LCSW, License Number: LC24279",
    "Licensed Psychotherapist in DC — LICSW, License Number: LC200003833",
  ];

  return (
    <div className="pt-20" data-testid="about-page">
      <SEO
        title="About Laura A. Jeanis, LCSW, LICSW, CCTP, CEO"
        metaTitle={seoOverride?.meta_title}
        keywords={[seoOverride?.focus_keyword, ...(seoOverride?.secondary_keywords || [])]}
        description={seoOverride?.meta_description || "Meet Laura A. Jeanis — Licensed Psychotherapist with 11+ years of clinical experience and a Certified Clinical Trauma Professional specializing in Ketamine-Assisted Psychotherapy."}
        imageAlt={seoOverride?.image_alt}
        pathname="/about"
      />
      {/* Hero Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-foreground leading-tight">
                Compassionate <span className="italic text-primary">Mental Wellness</span> Care
              </h1>
              <h2
                className="mt-4 font-serif text-lg md:text-xl lg:text-2xl italic font-medium text-primary leading-snug"
                data-testid="about-hero-subheading"
              >
                Meet Laura A. Jeanis, LCSW — Founder &amp; Licensed Ketamine Psychotherapist
              </h2>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                At Divine Love Wellness Enterprises, we believe everyone deserves access to transformative 
                mental health care. Our mission is to provide safe, effective Ketamine-Assisted Psychotherapy 
                in a nurturing environment where healing can flourish.
              </p>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                With 11 years of clinical experience and specialized trauma training, 
                we've helped countless clients find relief and renewed hope.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-3xl overflow-hidden shadow-soft">
                <img
                  src="https://images.unsplash.com/photo-1735013531564-a419dc5f31ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjB0aGVyYXB5JTIwY2xpbmljJTIwaW50ZXJpb3IlMjB3YXJtJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzcwOTE3NTE5fDA&ixlib=rb-4.1.0&q=85"
                  alt="Our modern therapy clinic"
                  className="w-full h-[400px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-accent p-6 rounded-2xl shadow-hover">
                <p className="font-serif text-3xl font-bold text-foreground">11 Years</p>
                <p className="text-sm text-foreground/80">Of Experience</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Our Core Values
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              These principles guide every interaction and treatment decision at our practice.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-8 h-full bg-white border-border/40 hover:border-primary/30 transition-colors duration-300 rounded-2xl flex gap-6">
                  <div className="p-3 rounded-full bg-secondary h-fit">
                    <value.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-foreground mb-2">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Team */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Meet Your Therapist
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
              Dedicated to your mental wellness with compassion and expertise.
            </p>
          </motion.div>

          {team.map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Card className="p-0 overflow-hidden bg-white border-0 rounded-3xl shadow-soft">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="h-[300px] md:h-auto">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-10 md:p-12 flex flex-col justify-center">
                    <h3 className="font-serif text-2xl font-semibold text-foreground">
                      {member.name}
                    </h3>
                    <p className="text-primary font-medium mt-1">{member.role}</p>
                    <p className="text-sm text-muted-foreground mt-1">{member.credentials}</p>
                    <p className="mt-6 text-muted-foreground leading-relaxed">
                      {member.bio}
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Credentials & Licenses
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Licensed and certified to provide the highest quality of care.
            </p>
          </motion.div>

          {/* CCTP Featured Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-12 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8 bg-secondary/50 rounded-2xl p-8 md:p-10 max-w-3xl mx-auto"
            data-testid="about-cctp-featured"
          >
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/i3y2ziu3_IMG_6346.jpeg"
              alt="Evergreen Certified — Certified Clinical Trauma Professional (CCTP)"
              className="h-32 w-32 md:h-40 md:w-40 rounded-full object-cover ring-4 ring-accent/30 shadow-soft flex-shrink-0"
              loading="lazy"
              data-testid="about-cctp-badge"
            />
            <div className="text-center md:text-left">
              <p className="text-xs uppercase tracking-wider text-accent font-medium mb-2">
                Evergreen Certified
              </p>
              <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground">
                Certified Clinical Trauma Professional (CCTP)
              </h3>
              <p className="mt-1 text-sm font-medium text-primary" data-testid="about-cctp-number">
                Certification No. 244805
              </p>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                Nationally recognized certification through Evergreen Certifications, demonstrating advanced expertise in trauma-informed clinical practice, evidence-based interventions, and specialized care for complex trauma.
              </p>
            </div>
          </motion.div>

          {/* Psychology Today Verification */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="mb-12 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6"
            data-testid="about-psychology-today-section"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
              Also Verified By
            </span>
            <a
              href="https://www.psychologytoday.com/profile/1844694"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center transition-opacity hover:opacity-80"
              aria-label="View Laura A. Jeanis verified profile on Psychology Today"
            >
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/semz0occ_IMG_4071.jpeg"
                alt="Verified by Psychology Today"
                className="h-14 w-auto rounded-md shadow-sm"
                loading="lazy"
                data-testid="about-psychology-today-badge"
              />
            </a>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <div className="p-6 bg-secondary/50 rounded-xl text-center">
                  <Award className="h-6 w-6 text-accent mx-auto mb-3" />
                  <p className="text-sm font-medium text-foreground">{cert}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-14 max-w-3xl mx-auto rounded-3xl border border-primary/15 bg-white/90 backdrop-blur shadow-soft px-6 md:px-10 py-8 md:py-10 text-center"
            data-testid="about-verify-license-box"
          >
            <ShieldCheck className="h-8 w-8 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground leading-tight">
              Verify Professional Licensure
            </h2>
            <h3 className="mt-4 font-serif text-lg sm:text-xl md:text-2xl italic font-medium text-primary leading-snug">
              Licensed Clinical Social Worker in FL &amp; ME Licensed Independent Clinical Social Worker in D.C.
            </h3>
            <a
              href="https://www.aswb.org/licenses/protecting-the-public/look-up-license/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-8"
              data-testid="about-verify-license-btn"
            >
              <Button
                size="lg"
                className="rounded-full px-8 py-6 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Verify License Status Online
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Ready to Learn More?
            </h2>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
              Our team is here to answer your questions and help you understand 
              if ketamine therapy could be right for you.
            </p>
            <div className="mt-10">
              <a href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting" target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg" 
                  className="rounded-full px-10 py-6 text-lg btn-scale shadow-soft bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="about-cta"
                >
                  Get in Touch
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
